import { FC } from "react";
import styled from "styled-components";
import { useLoaderData, useNavigate, LoaderFunctionArgs } from "react-router-dom";
import CommonWrapper from "../components/ui_components/CommonComponentsStructureCss";
import MainContainer from "../components/ui_components/MainContainer";
import { apiGetBeltById } from "../components/ui_components/ApiList";
import type { Belt } from "../types";

interface BeltWrestler {
    id: number;
    name: string;
    avatar_path: string;
    date_win: string;
    ppv_name: string;
    ppv_id: number;
    ppv_img_path?: string;
}

interface BeltData extends Belt {
    wrestlers: BeltWrestler[];
    is_active?: boolean;
}

const HallOfFameContainer = styled.div`
    padding: 2rem;
    max-width: 1100px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const BeltHeader = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 32px 24px 24px;
    background: linear-gradient(160deg, rgba(163, 27, 27, 0.12) 0%, rgba(10, 10, 10, 0.6) 100%);
    border: 1px solid rgba(163, 27, 27, 0.35);
    border-radius: 12px;
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 3px;
        background: linear-gradient(90deg, transparent 0%, rgba(163, 27, 27, 0.7) 40%, rgba(163, 27, 27, 0.7) 60%, transparent 100%);
    }

    h1 {
        color: rgba(255, 255, 255, 0.92);
        font-size: 28px;
        font-weight: 700;
        letter-spacing: 3px;
        text-transform: uppercase;
        margin: 0;
        text-align: center;
    }

    img.belt-img {
        max-width: 35%;
        max-height: 160px;
        object-fit: contain;
        filter: drop-shadow(0 4px 12px rgba(255, 215, 0, 0.2));
    }
`;

const StatusBadge = styled.span<{ $active: boolean }>`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 14px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
    border: 1px solid ${p => p.$active ? 'rgba(40, 167, 69, 0.6)' : 'rgba(255,255,255,0.2)'};
    color: ${p => p.$active ? '#28a745' : 'rgba(255,255,255,0.4)'};

    &::before {
        content: '';
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background: ${p => p.$active ? '#28a745' : 'rgba(255,255,255,0.25)'};
    }
`;

const ChampionsList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const ChampionCard = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.07);
    border-radius: 10px;
    overflow: hidden;
    min-height: 120px;
    transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;

    &:hover {
        border-color: rgba(163, 27, 27, 0.5);
        box-shadow: 0 4px 18px rgba(0, 0, 0, 0.3);
        transform: translateY(-2px);
    }

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 2px;
        background: linear-gradient(90deg, transparent 0%, rgba(163, 27, 27, 0.45) 40%, rgba(163, 27, 27, 0.45) 60%, transparent 100%);
    }
`;

const WrestlerSection = styled.div`
    display: flex;
    align-items: center;
    gap: 14px;
    width: 30%;
    padding: 0 20px;
    flex-shrink: 0;
`;

const Avatar = styled.div`
    width: 72px;
    height: 72px;
    border: 2px solid rgba(163, 27, 27, 0.4);
    border-radius: 6px;
    overflow: hidden;
    flex-shrink: 0;
    cursor: pointer;
    transition: border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: top;
        display: block;
    }

    &:hover {
        border-color: rgba(163, 27, 27, 0.85);
        transform: scale(1.06);
        box-shadow: 0 0 10px rgba(163, 27, 27, 0.25);
    }
`;

const VacantPlaceholder = styled.div`
    width: 72px;
    height: 72px;
    border: 2px dashed rgba(255, 255, 255, 0.15);
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-size: 9px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: rgba(255, 255, 255, 0.25);
`;

const WrestlerName = styled.span`
    font-size: 15px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 1.5px;
    color: rgba(255, 255, 255, 0.88);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const DateSection = styled.div`
    flex: 1;
    text-align: center;
    font-size: 13px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.45);
    letter-spacing: 0.5px;
`;

const PpvSection = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0;
    width: 28%;
    padding: 0 16px;
    flex-shrink: 0;

    .ppv-image {
        width: 70px;
        height: 90px;
        border-radius: 4px;
        object-fit: contain;
        object-position: center;
        display: block;
        margin: 0 auto;
        border: 1px solid rgba(163, 27, 27, 0.35);
        cursor: pointer;
        flex-shrink: 0;
        background: rgba(0, 0, 0, 0.3);
        transition: border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;

        &:hover {
            border-color: rgba(163, 27, 27, 0.8);
            transform: scale(1.06);
            box-shadow: 0 2px 8px rgba(163, 27, 27, 0.2);
        }
    }

    .ppv-name {
        font-size: 11px;
        font-weight: 600;
        color: rgba(255, 255, 255, 0.5);
        text-align: center;
        margin: 0;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 100%;
    }
`;

const BeltHallOfFame: FC = () => {
    const loaderData = useLoaderData() as { data: BeltData };
    const beltData = loaderData.data;
    const navigate = useNavigate();

    const navigateToWrestler = (wrestler: BeltWrestler): void => {
        navigate(`/wrestlers/i_${wrestler.id}_d_${wrestler.name}`);
    };

    const navigateToPpv = (ppvId: number, ppvName: string): void => {
        navigate(`/ppv/specific/i_${ppvId}_d_${ppvName}`);
    };

    const isVacant = (wrestler: BeltWrestler) => !wrestler.name || wrestler.name === 'VACANTE';

    return (
        <CommonWrapper>
            <MainContainer>
                <HallOfFameContainer>
                    <BeltHeader>
                        <h1>{beltData.name}</h1>
                        <img
                            className="belt-img"
                            src={process.env.REACT_APP_STORAGE_URL + beltData.img_path}
                            alt={beltData.name}
                        />
                        <StatusBadge $active={!!beltData.is_active}>
                            {beltData.is_active ? 'Attiva' : 'Ritirata'}
                        </StatusBadge>
                    </BeltHeader>

                    <ChampionsList>
                        {beltData.wrestlers && beltData.wrestlers.map((wrestler, idx) => (
                            <ChampionCard key={idx}>
                                <WrestlerSection>
                                    {isVacant(wrestler) ? (
                                        <VacantPlaceholder>Vacante</VacantPlaceholder>
                                    ) : (
                                        <Avatar onClick={() => navigateToWrestler(wrestler)}>
                                            <img
                                                src={process.env.REACT_APP_STORAGE_URL + wrestler.avatar_path}
                                                alt={wrestler.name}
                                            />
                                        </Avatar>
                                    )}
                                    <WrestlerName>
                                        {isVacant(wrestler) ? 'Vacante' : wrestler.name}
                                    </WrestlerName>
                                </WrestlerSection>

                                <DateSection>{wrestler.date_win}</DateSection>

                                <PpvSection>
                                    {wrestler.ppv_img_path && (
                                        <img
                                            src={process.env.REACT_APP_STORAGE_URL + wrestler.ppv_img_path}
                                            alt={wrestler.ppv_name}
                                            className="ppv-image"
                                            onClick={() => navigateToPpv(wrestler.ppv_id, wrestler.ppv_name)}
                                        />
                                    )}
                                    {wrestler.ppv_name && (
                                        <p className="ppv-name">{wrestler.ppv_name}</p>
                                    )}
                                </PpvSection>
                            </ChampionCard>
                        ))}
                    </ChampionsList>
                </HallOfFameContainer>
            </MainContainer>
        </CommonWrapper>
    );
};

export async function loader({ params }: LoaderFunctionArgs) {
    const response = await apiGetBeltById(Number(params.id));
    return response;
}

export default BeltHallOfFame;
